import sidebarHTML from "./sidebar.html?raw";
import { authStore, type AuthState } from "@/core/store/auth.store";

const SIDEBAR_PINNED_KEY = "sidebarPinned";
const SIDEBAR_TRANSITION_MS = 260;
const DESKTOP_SIDEBAR_QUERY = "(min-width: 769px) and (hover: hover) and (pointer: fine)";
const MOBILE_SIDEBAR_QUERY = "(max-width: 768px)";

interface SidebarElements {
  sidebar: HTMLElement;
  mainContent: HTMLElement;
  toggle: HTMLButtonElement;
  indicatorTrack: HTMLElement;
  indicator: HTMLElement;
}

let indicatorFrame: number | null = null;
let sidebarCleanup: (() => void) | null = null;

function scheduleIndicatorTransition(indicator: HTMLElement): void {
  if (indicatorFrame !== null) window.cancelAnimationFrame(indicatorFrame);
  indicatorFrame = window.requestAnimationFrame(() => {
    indicator.classList.remove("sidebar-active-indicator--instant");
    indicator.classList.add("sidebar-active-indicator--ready");
    indicatorFrame = null;
  });
}

function moveActiveIndicator(
  sidebar: HTMLElement,
  indicatorTrack: HTMLElement,
  indicator: HTMLElement,
  activeElement: HTMLElement | null,
  animate: boolean,
): void {
  indicator.classList.toggle("sidebar-active-indicator--visible", Boolean(activeElement));
  if (!activeElement || window.matchMedia(MOBILE_SIDEBAR_QUERY).matches) return;

  if (!animate || !indicator.classList.contains("sidebar-active-indicator--ready")) {
    indicator.classList.add("sidebar-active-indicator--instant");
  }

  const indicatorTrackRect = indicatorTrack.getBoundingClientRect();
  const elemRect = activeElement.getBoundingClientRect();
  const indicatorY = Math.round((elemRect.top - indicatorTrackRect.top) * 100) / 100;

  let targetHeight = elemRect.height;
  if (activeElement.id === "sidebarGroupGastos" && activeElement.classList.contains("expanded")) {
    const toggle = activeElement.querySelector<HTMLElement>("#sidebarGastosToggle");
    const submenu = activeElement.querySelector<HTMLElement>("#sidebarGastosSubmenu");
    if (toggle && submenu) {
      const submenuStyle = window.getComputedStyle(submenu);
      const submenuMargins = Number.parseFloat(submenuStyle.getPropertyValue("--sidebar-submenu-gap-before"))
        + Number.parseFloat(submenuStyle.getPropertyValue("--sidebar-submenu-gap-after"));
      targetHeight = toggle.getBoundingClientRect().height + submenu.scrollHeight + submenuMargins;
    }
  }
  const indicatorHeight = Math.round(targetHeight * 100) / 100;

  sidebar.style.setProperty("--sidebar-indicator-y", `${indicatorY}px`);
  sidebar.style.setProperty("--sidebar-indicator-height", `${indicatorHeight}px`);

  if (indicator.classList.contains("sidebar-active-indicator--instant")) {
    scheduleIndicatorTransition(indicator);
  }
}

function updateSidebarUserData(state: AuthState): void {
  const userNameElement = document.getElementById("sidebarUserNameContextual");
  if (!userNameElement) return;

  userNameElement.textContent = state.isLoggedIn && state.user
    ? `${state.user.nombre} ${state.user.apellido || ""}`.trim()
    : "Usuario";
}

function normalizePath(path: string): string {
  const cleanPath = path.split("?")[0].split("#")[0].replace(/\.html$/, "");
  if (!cleanPath || cleanPath === "/index") return "/";
  return cleanPath.length > 1 && cleanPath.endsWith("/")
    ? cleanPath.slice(0, -1)
    : cleanPath;
}

function matchesPath(currentPath: string, candidate: string): boolean {
  if (candidate === "/") return currentPath === "/";
  return currentPath === candidate || currentPath.startsWith(`${candidate}/`);
}

function getActivePaths(link: HTMLAnchorElement): string[] {
  return (link.dataset.activePaths || link.pathname)
    .split(",")
    .map((path) => normalizePath(path.trim()))
    .filter(Boolean);
}

let lastGastosPath = "/egresosnegocio";

/** Mantiene la selección visual conectada con la ruta real de la SPA. */
function updateActiveLink(path = window.location.pathname): void {
  const currentPath = normalizePath(path);
  const sidebar = document.getElementById("appSidebar");
  const indicatorTrack = document.getElementById("sidebarActiveTrack");
  const indicator = document.getElementById("sidebarActiveIndicator");
  if (!sidebar || !indicatorTrack || !indicator) return;

  const gastosGroup = document.getElementById("sidebarGroupGastos");
  const gastosToggle = document.getElementById("sidebarGastosToggle");
  const gastosTitle = document.getElementById("sidebarGastosTitle");
  const links = Array.from(sidebar.querySelectorAll<HTMLAnchorElement>("a[data-link]"));
  let activeLink: HTMLAnchorElement | null = null;
  let isGastosRoute = false;

  for (const link of links) {
    const isActive = getActivePaths(link).some((p) => matchesPath(currentPath, p));
    link.classList.toggle("active", isActive);
    if (isActive) {
      link.setAttribute("aria-current", "page");
      activeLink = link;
      if (link.closest("#sidebarGroupGastos")) {
        isGastosRoute = true;
      }
    } else {
      link.removeAttribute("aria-current");
    }
  }

  // Comprobar si la ruta actual es Gastos (excluyendo Billetera /otrosingresos)
  if (currentPath === "/egresosnegocio" || currentPath === "/gastospersonales") {
    isGastosRoute = true;
  }

  // Actualizar el título contextual dinámico de Gastos y recordar la última ruta de gastos visitada
  if (gastosTitle) {
    if (currentPath === "/gastospersonales") {
      gastosTitle.textContent = "Gastos Personales";
      lastGastosPath = "/gastospersonales";
    } else if (currentPath === "/egresosnegocio") {
      gastosTitle.textContent = "Gastos del Negocio";
      lastGastosPath = "/egresosnegocio";
    } else {
      // Fuera de la sección de Gastos, siempre muestra "Gastos"
      gastosTitle.textContent = "Gastos";
    }
  }

  const isCollapsedSidebar = sidebar.classList.contains("collapsed");

  // Si estamos en una ruta de Gastos, marcar el grupo como activo
  if (gastosGroup && gastosToggle) {
    if (isGastosRoute) {
      gastosGroup.classList.add("active");
      gastosToggle.classList.add("active");

      const isExpanded = gastosGroup.classList.contains("expanded");
      gastosToggle.setAttribute("aria-expanded", String(isExpanded));
      sidebar.classList.toggle("gastos-expanded", isExpanded);
    } else {
      gastosGroup.classList.remove("active", "expanded");
      gastosToggle.classList.remove("active");
      gastosToggle.setAttribute("aria-expanded", "false");
      sidebar.classList.remove("gastos-expanded");
    }
  }

  // En modo expandido, la cápsula toma la altura real del grupo; así conserva
  // la alineación aunque la interfaz se compacte en pantallas de menor altura.
  let targetElementForIndicator: HTMLElement | null = activeLink;
  if (isGastosRoute && gastosGroup && gastosToggle) {
    const isExpanded = gastosGroup.classList.contains("expanded");
    targetElementForIndicator = isCollapsedSidebar || !isExpanded ? gastosToggle : gastosGroup;
  }

  moveActiveIndicator(sidebar, indicatorTrack, indicator, targetElementForIndicator, true);

  if (activeLink && window.matchMedia(MOBILE_SIDEBAR_QUERY).matches) {
    activeLink.scrollIntoView({ block: "nearest", inline: "center" });
  }
}

/** API mínima para historias y superficies que controlan su propia URL. */
export function updateSidebarActivePath(path: string): void {
  updateActiveLink(path);
}

function readPinnedPreference(): boolean {
  try {
    // Limpiar rastro viejo en localStorage para evitar que arranque expandido
    if (window.localStorage.getItem(SIDEBAR_PINNED_KEY) !== null) {
      window.localStorage.removeItem(SIDEBAR_PINNED_KEY);
    }
    return window.sessionStorage.getItem(SIDEBAR_PINNED_KEY) === "true";
  } catch {
    return false;
  }
}

function savePinnedPreference(isPinned: boolean): void {
  try {
    window.sessionStorage.setItem(SIDEBAR_PINNED_KEY, String(isPinned));
  } catch {
    // El sidebar sigue funcionando aunque el navegador bloquee el almacenamiento.
  }
}

function getSidebarElements(): SidebarElements | null {
  const sidebar = document.getElementById("appSidebar");
  const mainContent = document.querySelector<HTMLElement>(".main-content-wrapper");
  const toggle = document.getElementById("sidebarToggleBtn");
  const indicatorTrack = document.getElementById("sidebarActiveTrack");
  const indicator = document.getElementById("sidebarActiveIndicator");

  if (
    !sidebar ||
    !mainContent ||
    !(toggle instanceof HTMLButtonElement) ||
    !indicatorTrack ||
    !indicator
  ) {
    return null;
  }

  return { sidebar, mainContent, toggle, indicatorTrack, indicator };
}

function setSidebarState(
  elements: SidebarElements,
  collapsed: boolean,
  pinned: boolean,
): void {
  elements.sidebar.classList.toggle("collapsed", collapsed);
  elements.sidebar.classList.toggle("pinned-expanded", pinned);
  elements.mainContent.classList.toggle("sidebar-collapsed", collapsed);
  elements.toggle.setAttribute("aria-expanded", String(!collapsed));
  elements.toggle.setAttribute(
    "aria-label",
    collapsed ? "Expandir menú principal" : "Contraer menú principal",
  );

  if (collapsed) {
    const gastosGroup = document.getElementById("sidebarGroupGastos");
    const gastosToggle = document.getElementById("sidebarGastosToggle");
    if (gastosGroup && gastosToggle) {
      gastosGroup.classList.remove("expanded");
      gastosToggle.setAttribute("aria-expanded", "false");
      elements.sidebar.classList.remove("gastos-expanded");
    }
  }

  updateActiveLink();
}

function notifySidebarTransition(eventName: "sidebar-toggling" | "sidebar-toggled", pinned: boolean): void {
  window.dispatchEvent(new CustomEvent(eventName, { detail: { pinned } }));
}

/** Configura fijación por botón, expansión automática por hover y navegación por teclado. */
function setupSidebarLogic(): () => void {
  const elements = getSidebarElements();
  if (!elements) {
    console.warn("No se encontraron los elementos necesarios para inicializar el sidebar.");
    return () => undefined;
  }

  const desktopQuery = window.matchMedia(DESKTOP_SIDEBAR_QUERY);
  let isSidebarPinned = readPinnedPreference();
  let hoverTimer: number | null = null;
  let transitionTimer: number | null = null;

  const clearHoverTimer = (): void => {
    if (hoverTimer === null) return;
    window.clearTimeout(hoverTimer);
    hoverTimer = null;
  };

  const showCollapsedDefault = (): void => {
    const shouldCollapse = desktopQuery.matches && !isSidebarPinned;
    setSidebarState(elements, shouldCollapse, isSidebarPinned);
  };

  const previewExpanded = (): void => {
    if (isSidebarPinned || !desktopQuery.matches) return;
    clearHoverTimer();
    hoverTimer = window.setTimeout(() => {
      setSidebarState(elements, false, false);
      hoverTimer = null;
    }, 60);
  };

  const restoreCollapsed = (): void => {
    if (isSidebarPinned || !desktopQuery.matches) return;
    clearHoverTimer();
    setSidebarState(elements, true, false);
  };

  const handleToggle = (): void => {
    isSidebarPinned = !isSidebarPinned;
    savePinnedPreference(isSidebarPinned);
    clearHoverTimer();
    notifySidebarTransition("sidebar-toggling", isSidebarPinned);
    setSidebarState(elements, !isSidebarPinned && desktopQuery.matches, isSidebarPinned);
    if (transitionTimer !== null) window.clearTimeout(transitionTimer);
    transitionTimer = window.setTimeout(
      () => notifySidebarTransition("sidebar-toggled", isSidebarPinned),
      SIDEBAR_TRANSITION_MS,
    );
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape" && !elements.sidebar.classList.contains("collapsed")) {
      isSidebarPinned = false;
      savePinnedPreference(false);
      clearHoverTimer();
      elements.toggle.focus();
      setSidebarState(elements, true, false);
      notifySidebarTransition("sidebar-toggled", false);
    }
  };

  elements.toggle.addEventListener("click", handleToggle);
  elements.sidebar.addEventListener("mouseenter", previewExpanded);
  elements.sidebar.addEventListener("mouseleave", restoreCollapsed);
  elements.sidebar.addEventListener("keydown", handleKeyDown);

  const gastosGroup = document.getElementById("sidebarGroupGastos");
  const gastosToggle = document.getElementById("sidebarGastosToggle");
  const sidebar = elements.sidebar;

  let handleGastosToggle: ((event: MouseEvent) => void) | null = null;

  if (gastosToggle && gastosGroup) {
    handleGastosToggle = (event: MouseEvent): void => {
      event.preventDefault();
      if (elements.sidebar.classList.contains("collapsed")) {
        setSidebarState(elements, false, isSidebarPinned);
      }

      const currentPath = normalizePath(window.location.pathname);
      const isAlreadyInGastos =
        currentPath === "/egresosnegocio" ||
        currentPath === "/gastospersonales";

      // 1er Clic (desde otra sección): navegar directamente a Gastos con submenú cerrado (44px)
      if (!isAlreadyInGastos) {
        gastosGroup.classList.remove("expanded");
        gastosToggle.setAttribute("aria-expanded", "false");
        sidebar.classList.remove("gastos-expanded");
        const targetPath = lastGastosPath || "/egresosnegocio";
        history.pushState(null, "", targetPath);
        window.dispatchEvent(new CustomEvent("route-changed", { detail: { path: targetPath } }));
        return;
      }

      // 2do Clic (estando dentro de Gastos): alternar la apertura/cierre del submenú (130px)
      const isExpanded = gastosGroup.classList.toggle("expanded");
      gastosToggle.setAttribute("aria-expanded", String(isExpanded));
      sidebar.classList.toggle("gastos-expanded", isExpanded);

      const targetElementForIndicator = isExpanded ? gastosGroup : gastosToggle;
      moveActiveIndicator(sidebar, elements.indicatorTrack, elements.indicator, targetElementForIndicator, true);
    };
    gastosToggle.addEventListener("click", handleGastosToggle);
  }

  desktopQuery.addEventListener("change", showCollapsedDefault);
  showCollapsedDefault();

  return () => {
    clearHoverTimer();
    if (transitionTimer !== null) window.clearTimeout(transitionTimer);
    elements.toggle.removeEventListener("click", handleToggle);
    elements.sidebar.removeEventListener("mouseenter", previewExpanded);
    elements.sidebar.removeEventListener("mouseleave", restoreCollapsed);
    elements.sidebar.removeEventListener("keydown", handleKeyDown);
    if (gastosToggle && handleGastosToggle) {
      gastosToggle.removeEventListener("click", handleGastosToggle);
    }
    desktopQuery.removeEventListener("change", showCollapsedDefault);
  };
}

function setupNavigationListeners(): () => void {
  let resizeFrame: number | null = null;
  const handleRouteChange = (): void => updateActiveLink();
  const handleResize = (): void => {
    if (resizeFrame !== null) return;
    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = null;
      updateActiveLink();
    });
  };

  const handleBodyClick = (event: MouseEvent): void => {
    if (!(event.target instanceof Element)) return;

    // Al seleccionar una subsección (Personales o Negocio), cerrar automáticamente el submenú y reducir la cápsula
    const sublink = event.target.closest<HTMLAnchorElement>("#appSidebar .sidebar-sublink");
    if (sublink) {
      const gastosGroup = document.getElementById("sidebarGroupGastos");
      const gastosToggle = document.getElementById("sidebarGastosToggle");
      const sidebar = document.getElementById("appSidebar");
      if (gastosGroup && gastosToggle && sidebar) {
        gastosGroup.classList.remove("expanded");
        gastosToggle.setAttribute("aria-expanded", "false");
        sidebar.classList.remove("gastos-expanded");
      }
    }

    const link = event.target.closest<HTMLAnchorElement>("#appSidebar a[data-link]");
    if (!link) return;
    const destination = new URL(link.href, window.location.origin);
    updateActiveLink(destination.pathname);
  };

  const sidebarNav = document.getElementById("sidebarPrimaryNavigation");
  const handleNavScroll = (): void => {
    if (resizeFrame !== null) return;
    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = null;
      updateActiveLink();
    });
  };

  sidebarNav?.addEventListener("scroll", handleNavScroll, { passive: true });
  window.addEventListener("route-changed", handleRouteChange);
  window.addEventListener("popstate", handleRouteChange);
  window.addEventListener("resize", handleResize);
  document.body.addEventListener("click", handleBodyClick);

  return () => {
    if (resizeFrame !== null) window.cancelAnimationFrame(resizeFrame);
    sidebarNav?.removeEventListener("scroll", handleNavScroll);
    window.removeEventListener("route-changed", handleRouteChange);
    window.removeEventListener("popstate", handleRouteChange);
    window.removeEventListener("resize", handleResize);
    document.body.removeEventListener("click", handleBodyClick);
  };
}

export function initSidebar(containerSelector: string): void {
  const container = document.querySelector<HTMLElement>(containerSelector);
  if (!container) {
    console.error(`Contenedor '${containerSelector}' para el sidebar no encontrado.`);
    return;
  }

  try {
    sidebarCleanup?.();
    container.innerHTML = sidebarHTML;
    const unsubscribeAuth = authStore.subscribe(updateSidebarUserData);
    const cleanupLogic = setupSidebarLogic();
    updateActiveLink();
    const cleanupNavigation = setupNavigationListeners();
    sidebarCleanup = () => {
      unsubscribeAuth();
      cleanupLogic();
      cleanupNavigation();
      if (indicatorFrame !== null) window.cancelAnimationFrame(indicatorFrame);
      indicatorFrame = null;
    };
  } catch (error) {
    console.error("Error al inicializar la barra lateral:", error);
    container.innerHTML = '<p class="p-4 text-red-600">No fue posible cargar la navegación.</p>';
  }
}
